import { Model, ModelNames, Models, createReducer, reduxApp } from './createStore';
import models from './models';
import basicModel from '@/store/basicModel';

export type InferModelProps<M extends Model> = M['state'] & ReturnType<M['actions']>;

export type BasicModel = InferModelProps<typeof basicModel>;

const injectedModelNames = new Set('basic');

const checkSameKey: (name: string, type: string, part: object, all: object) => void = (
  name,
  type,
  part,
  all,
) => {
  const except = type === 'state' ? 'now' : 'setState';
  if (typeof part === 'object') {
    Object.keys(part).forEach((key) => {
      if (key !== except && key in all && import.meta.env.DEV) {
        console.error(`使用 withStore 时，${name} 与其它 model 存在重名 \`${key}\` ${type}`); // eslint-disable-line
      }
    });
  }
};

export const injectModels = (...names: ModelNames[]) => {
  const newModels: Models = {};
  names.forEach((name) => {
    if (!injectedModelNames.has(name)) {
      injectedModelNames.add(name);
      newModels[name] = models[name];
    }
  });
  const newReducers = createReducer(newModels);
  Object.entries(newReducers).forEach(([name, reducer]) => {
    reduxApp.addReducer(name, reducer);
  });
};

/**
 * withStore
 * @param {string[]} names - 'modelA', 'modelB', ...
 * @return {array} [mapState, mapMethods]
 */
const withStore = <M>(
  ...names: ModelNames[]
): [(state: any) => M & BasicModel, (dispatch: any) => M & BasicModel] => {
  injectModels(...names);
  const namesLength = names.length;
  let stateCount = 0;
  let methodsCount = 0;

  let mergedState: any = { now: {} };
  let mergedMethods: any = {};
  return [
    (state: any) => {
      names.forEach((name) => {
        const modelState = state[name];

        if (stateCount < namesLength) {
          checkSameKey(name, 'state', modelState, mergedState);
          stateCount += 1;
        }

        mergedState = {
          ...mergedState,
          ...modelState,
          now: { ...(mergedState?.now || {}), ...(modelState?.now || {}) },
        };
      });
      return mergedState;
    },
    (dispatch: any) => {
      names.forEach((name) => {
        const modelMethods = dispatch[name];

        if (methodsCount < namesLength) {
          checkSameKey(name, 'action', modelMethods, mergedMethods);
          methodsCount += 1;
        }

        mergedMethods = { ...mergedMethods, ...modelMethods };
      });
      return mergedMethods;
    },
  ];
};

export default withStore;
